import {
	Box,
	Card,
	CardContent,
	Grid,
	InputAdornment,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { AppLayout } from "../../container/layout/app";
import { AdminManagementWrapper } from "./styled";
import { BaseButton } from "../../component/button/styled";
import { AdminTable } from "../../container/table/admintable";
import { useContext, useEffect, useState } from "react";
import { AddUserForm } from "../../container/form/adduser";
import { AppContext } from "../../context";
import { BaseAlertModal } from "../../component/modal/alert";
import { SearchIcon, SuccessModalIcon } from "../../asset";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllUserByTypeService } from "../../util/usermanagement/user/retrieveAllUserByType";
import { useNavigate } from "react-router-dom";
import { BaseInput } from "../../component/form/input/styled";
import { useDebounce } from "use-debounce";

export const AdminManagement = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setIsAddUserFormModalOpen, isAlertModalOpen, setIsAlertModalOpen } =
		useContext(AppContext);

	const [formDetails, setFormDetails] = useState({
		query: "",
	});
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "5",
		totalPages: "1",
	});

	const [debouncedQueryRef] = useDebounce(formDetails.query, 500);

	const { data: admins, refetch: refetchAdmins } = useQuery({
		queryKey: [
			`admin-and-distributor-accounts`,
			TOKEN,
			paginationIndex,
			debouncedQueryRef,
		],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllUserByTypeService(
				TOKEN,
				{
					admin: true,
					distributor: true,
				},
				debouncedQueryRef,
				paginationIndex,
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	useEffect(() => {
		if (!isAlertModalOpen) {
			refetchAdmins();
		}
	}, [isAlertModalOpen, refetchAdmins]);

	const handleNavigateToDetails = (
		e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
		id: string | Record<any, any>,
	) => {
		e.preventDefault();
		return navigate(`/admin-management/${id}`);
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	const handleOpenAddUserFormModal = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return setIsAddUserFormModalOpen(true);
	};

	const handleAlertModalClickOutside = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return setIsAlertModalOpen(false);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	return (
		<AppLayout pageId="Admin-Management">
			<AdminManagementWrapper>
				<AddUserForm />
				<BaseAlertModal
					callToAction="Close"
					open={isAlertModalOpen}
					icon={<SuccessModalIcon />}
					title="User added successfully"
					className="add-admin-successful-modal"
					handleClose={handleAlertModalClickOutside}
					callToActionBgColor="var(--success-color)"
					handleCallToAction={handleAlertModalCallToActionClick}
					message="The user has been successfully onboarded. User can now log in with their credentials."
				/>
				<Grid
					container
					component={"div"}
					spacing={{
						mobile: "calc(var(--flex-gap)/2)",
						laptop: "var(--flex-gap)",
					}}
					alignItems={{ laptop: "center" }}
				>
					<Grid size={{ mobile: 12, laptop: 4, desktop: 6 }}>
						<Box>
							<Typography
								variant="h1"
								fontFamily={"Roboto"}
								fontWeight={600}
								fontSize={28}
								lineHeight={"normal"}
								color="var(--dark-color)"
							>
								Users
							</Typography>
						</Box>
					</Grid>
					<Grid size={{ mobile: 12, laptop: 8, desktop: 6 }}>
						<Grid
							container
							component={"div"}
							flexWrap={{ miniTablet: "nowrap" }}
							spacing={{
								mobile: "calc(var(--flex-gap)/2)",
							}}
							alignItems={{ miniTablet: "center" }}
						>
							<Grid
								flexGrow={{ mobile: 1 }}
								size={{ mobile: 12, miniTablet: 8 }}
							>
								<Box overflow={"hidden"} flex={1} height={"100%"}>
									<BaseInput
										startAdornment={
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										}
										name="query"
										border={"none"}
										borderradius={"6px"}
										value={formDetails.query}
										placeholder="Search name, email, type, status, organization"
										bgcolor="var(--light-color)"
										onChange={(e) => handleChange(e)}
										padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
									/>
								</Box>
							</Grid>
							<Grid
							// flexGrow={{ mobile: 1 }}
							// size={{ mobile: 12, miniTablet: 4 }}
							>
								<Box overflow={"hidden"}>
									<BaseButton
										variant="contained"
										disableElevation
										sx={{
											width: "100%",
											borderRadius: "6px",
											padding:
												"calc(var(--basic-padding)/3.2) calc(var(--basic-padding)/2)",
										}}
										onClick={handleOpenAddUserFormModal}
									>
										<Typography
											variant={"button"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"inherit"}
											textTransform={"inherit"}
										>
											Create User
										</Typography>
									</BaseButton>
								</Box>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Stack gap={"calc(var(--flex-gap)/2)"}>
					<Card
						sx={{
							border: "none",
							borderRadius: "12px",
						}}
					>
						<CardContent className="card-content">
							<Box component={"div"} className="admin-table-box">
								<AdminTable
									rows={admins}
									handleViewDetailsClick={handleNavigateToDetails}
								/>
							</Box>
						</CardContent>
					</Card>
					<Grid container component={"div"} spacing={"var(--flex-gap)"}>
						<Grid
							size={{ mobile: 12, laptop: 8 }}
							display={{ mobile: "none", laptop: "grid" }}
						></Grid>
						<Grid size={{ mobile: 12, laptop: 4 }}>
							<Stack
								direction={"row"}
								overflow={"hidden"}
								borderRadius={"6px"}
								alignItems={"center"}
								justifyContent={"space-around"}
								border={"1px solid var(--input-field-border-color)"}
							>
								<Box overflow={"hidden"} display={"flex"}>
									<BaseButton
										radius="0"
										border="none"
										variant="text"
										disableElevation
										colour={"var(--dark-color)"}
										padding="0 calc(var(--basic-padding)/2)"
										onClick={(e) => handlePagination(e, "previous")}
										disabled={
											parseInt(paginationIndex.page, 10) === 1 || isFetching
										}
									>
										<Typography
											variant={"button"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"inherit"}
											textTransform={"inherit"}
										>
											Previous
										</Typography>
									</BaseButton>
								</Box>
								<Box
									display={"flex"}
									overflow={"hidden"}
									bgcolor={"var(--primary-color)"}
									padding={"calc(var(--basic-padding)/2)"}
								>
									<Typography
										variant="h3"
										fontFamily={"Roboto"}
										fontWeight={600}
										fontSize={10}
										lineHeight={"normal"}
										color="var(--light-color)"
									>
										{`${isFetching ? "-" : paginationIndex.page} / ${isFetching ? "-" : paginationIndex.totalPages}`}
									</Typography>
								</Box>
								<Box overflow={"hidden"} display={"flex"}>
									<BaseButton
										radius="0"
										border="none"
										variant="text"
										disableElevation
										colour={"var(--dark-color)"}
										padding="0 calc(var(--basic-padding)/2)"
										onClick={(e) => handlePagination(e, "next")}
										disabled={
											paginationIndex.page === paginationIndex.totalPages ||
											isFetching
										}
									>
										<Typography
											variant={"button"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"inherit"}
											textTransform={"inherit"}
										>
											Next
										</Typography>
									</BaseButton>
								</Box>
							</Stack>
						</Grid>
					</Grid>
				</Stack>
			</AdminManagementWrapper>
		</AppLayout>
	);
};
